// deployment_class: customer_env_allowed
import { useEffect, useRef } from 'react';

/**
 * useLayerHistory — sync the dashboard's overlay layers to browser history so
 * the OS back gesture pops exactly ONE layer instead of leaving the app.
 *
 * The dashboard stacks overlays with plain `useState` — the project view
 * (`proj`) and the drill stack (`stack`) — with zero history integration
 * ({@link Dashboard} · Fable mobile strategy §3a diagnosis). On a phone the
 * universal "get me out of here" move is the edge-swipe back; today that
 * navigates the *browser* back and throws the CEO out of the dashboard entirely
 * (the UI_BUG_1a mobile dead-end class). This hook converts that gesture into
 * the primary, layer-aware back mechanism (§3b Primitive 2 · §6 A).
 *
 * Contract — **every opened layer is exactly one history entry, and every
 * dismissal resolves to exactly one layer pop:**
 * - `depth` = the number of poppable overlay layers currently open (the caller
 *   computes it, e.g. `(proj ? 1 : 0) + stack.length`; Ask-sheet joins in a
 *   later wave). When `depth` rises, we `pushState` one entry per new layer.
 * - `onPop` pops exactly one layer, top-first (drill before project). It fires
 *   on `popstate` (OS back / browser back) when any layer is open; at base
 *   (`depth === 0`) we let the browser navigate away normally.
 * - In-app closes (a ✕ button reducing `depth` directly) are reconciled by
 *   consuming the matching history entries via `history.go`, so the browser
 *   stack never drifts out of lockstep with the logical layer depth. The
 *   `popstate` those `go` calls echo back are swallowed so they don't
 *   double-pop.
 *
 * Desktop gets this too (free win): browser-back closes a drill instead of
 * leaving the app — the same trapdoor exists at every width, it just has
 * mouse-reachable alternatives on desktop.
 *
 * SSR/jsdom-safe: guards `window`/`history` presence and treats `scrollTo`-less
 * environments gracefully via optional access.
 */
export function useLayerHistory(depth: number, onPop: () => void): void {
  // The history-entry count we've reconciled to the logical layer depth.
  const syncedRef = useRef(0);
  // Number of pending popstate events that are echoes of our own in-app
  // history.go() reconciliation — swallow them so they don't pop layer state.
  const echoRef = useRef(0);
  // Keep onPop current without re-subscribing the popstate listener each render.
  const onPopRef = useRef(onPop);
  onPopRef.current = onPop;

  // Reconcile browser history with the logical layer depth.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.history) return;
    const synced = syncedRef.current;
    if (depth > synced) {
      for (let i = synced; i < depth; i += 1) {
        window.history.pushState({ kdDepth: i + 1 }, '');
      }
      syncedRef.current = depth;
    } else if (depth < synced) {
      // Adopt the new baseline BEFORE navigating so the echoed popstate(s) see
      // a consistent syncedRef and get swallowed rather than re-popping.
      const diff = synced - depth;
      syncedRef.current = depth;
      echoRef.current += diff;
      window.history.go(-diff);
    }
  }, [depth]);

  // A single popstate listener maps one OS-back / browser-back to one pop.
  useEffect(() => {
    const onPopState = (): void => {
      if (echoRef.current > 0) {
        // Echo of an in-app close's history.go — already reconciled; swallow.
        echoRef.current -= 1;
        return;
      }
      if (syncedRef.current > 0) {
        syncedRef.current -= 1;
        onPopRef.current();
      }
      // else: at base — the browser has already navigated; let it leave.
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
}
